import { useState } from "react";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isBefore,
  isToday,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { VscClose } from "react-icons/vsc";
import { Clock } from "lucide-react";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function getCalendarDays(month) {
  const start = startOfWeek(startOfMonth(month));
  const end = endOfWeek(endOfMonth(month));
  const days = [];
  let current = start;

  while (current <= end) {
    days.push(current);
    current = addDays(current, 1);
  }

  return days;
}

export default function EventDateTimePicker({
  selectedDate,
  onSelect,
  onClose,
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [date, setDate] = useState(selectedDate || null);
  const [time, setTime] = useState(selectedDate ? format(selectedDate, "HH:mm") : "12:00");

  const days = getCalendarDays(currentMonth);

  const handleDayClick = (day) => {
    setDate(day);
  };

  const handleTimeChange = (e) => {
    setTime(e.target.value);
  };

  const handleConfirm = () => {
    if (date) {
      const [hours, minutes] = time.split(":");
      const selectedDateTime = new Date(date);
      selectedDateTime.setHours(parseInt(hours));
      selectedDateTime.setMinutes(parseInt(minutes));
      onSelect?.(selectedDateTime);
      onClose()
    }
  };

  const nextMonth = addMonths(currentMonth, 1);

  return (
    <div className="bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-xl border p-8 w-full md:max-w-[360px]">
      
      {/* Close */}
      <div className="flex justify-end">
        <VscClose
          fontSize={24}
          className="cursor-pointer"
          onClick={onClose}
        />
      </div>

      {/* Selected Date Display */}
      <div className="text-center mb-6">
        <p className="text-xs font-semibold uppercase">Event Date</p>
        <p className="text-lg font-semibold mt-1">
          {date ? format(date, "MMM d, yyyy") : "Select date"}
        </p>
      </div>

      {/* Time Picker */}
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase mb-2">Event Time</p>
        <div className="flex items-center justify-center gap-2">
          <Clock size={16} className="text-gray-400" />
          <input
            type="time"
            value={time}
            onChange={handleTimeChange}
            className="bg-white text-primary font-semibold text-lg px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Calendar */}
      <div className="relative">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="absolute left-2 top-0"
        >
          <ChevronLeft />
        </button>

        <h3 className="text-center font-semibold mb-4">
          {format(currentMonth, "MMMM yyyy")}
        </h3>

        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="absolute right-2 top-0"
        >
          <ChevronRight />
        </button>

        <div className="grid grid-cols-7 gap-y-1">
          {WEEKDAYS.map((d, i) => (
            <div key={i} className="text-center text-xs text-gray-400 py-2">
              {d}
            </div>
          ))}

          {days.map((day, i) => {
            const inMonth = isSameMonth(day, currentMonth);
            const past = isBefore(day, new Date()) && !isToday(day);

            return (
              <button
                key={i}
                disabled={!inMonth || past}
                onClick={() => handleDayClick(day)}
                className={`
                  h-10 text-sm rounded-full
                  ${!inMonth ? "invisible" : ""}
                  ${past ? "text-gray-400" : "hover:bg-[#ffefc9]"}
                  ${
                    date && format(day, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
                      ? "bg-primary text-white"
                      : ""
                  }
                `}
              >
                {inMonth ? format(day, "d") : ""}
              </button>
            );
          })}
        </div>
      </div>

      {/* Confirm Button */}
      <button
        onClick={handleConfirm}
        disabled={!date}
        className="w-full bg-primary text-white font-semibold py-3 rounded-xl mt-6 hover:bg-primary/90 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        Confirm Selection
      </button>

    </div>
  );
}
